import React, { useEffect } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Tooltip,
  Paper,
  Chip,
  Select,
  MenuItem,
  CircularProgress
} from "@material-ui/core";
import Modal from '@material-ui/core/Modal';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CreateIcon from '@material-ui/icons/Create';
import FlagIcon from "@material-ui/icons/Flag";
import SignaturePad from "../common/ESign";
import Button from '@material-ui/core/Button';
import { Container } from "react-bootstrap";
import { deleteOnboardingDocument, downloadAgreement, getAgreementDetails, getDocumentStatusOptions, updateAgreementStatus, uploadAgreementDocuments } from "../../store/actions/userOnboarding";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { convertFilesToBase64, downloadPDF } from "../../config/utils";
import AttachFileIcon from '@material-ui/icons/AttachFile';
import moment from "moment";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import {withStyles } from '@material-ui/core/styles';
import InputBase from '@material-ui/core/InputBase';
import CancelIcon from '@material-ui/icons/Cancel';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import { allowedExtensions } from "../../constant";
import AlertDialogModal from "../common/AlertDialog";
import { clearLocalStorage, removeLocalStorageItem } from "../../services/StorageService";

const BootstrapInput = withStyles((theme) => ({
  root: {
    'label + &': {
      marginTop: theme.spacing(3),
    },
  },
  input: {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #ced4da',
    fontSize: 16,
    padding: '10px 26px 10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    // Use the system font instead of the default Roboto font.
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    '&:focus': {
      borderRadius: 4,
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
}))(InputBase);

const AgreementsESignatures = (props) => {
    const {isViewUserRequest, activeStep, handleBack, isApprovedSeller} = props;
    const permissionMetadata = useSelector((state) => state?.sidebar?.permissionMetadata);
    const hasOnboardingData = permissionMetadata?.hasOnboardingData !== false;
    const navigate = useNavigate();
    const [showLoader, setShowLoader] = React.useState(false);
    const [modalOpen, setModalOpen] = React.useState({
        open: false,
        agreement: null
    });
    const [showDialog, setShowDialog] = React.useState(false);
    const [agreements, setAgreements] = React.useState([]);
    const [documentStatusList, setDocumentStatusList] = React.useState([]);

    const toggleModal = (item) => {
        setModalOpen(prev => ({
            open: !prev.open,
            agreement: item || null
        }));
    };

    useEffect(() => {
        fetchAgreements();
        getDocumentStatusList();
    }, []);

    const fetchAgreements = async () => {
        let data = {
            "onboardingId": 0,
            "documentTypeId": 1,
            "countryId": 0
        }
        if (isViewUserRequest) {
            data.onboardingId = props.data.Id;
        }
        setShowLoader(true);
        let response = await getAgreementDetails(data, isApprovedSeller);
        if (response?.data?.Status === 200) {
            const list = response.data.EntityList || [];
            const normalized = list.map(a => ({ ...a, ESigned: !!a.ESigned }));
            setAgreements(normalized);
        }
        setShowLoader(false);
    };

    const handleCheckbox = (event, agreement) => {
        let updatedAgreements = agreements.map(a => a.DocumentMasterId === agreement.DocumentMasterId ? { ...a, ESigned: event.target.checked } : a);
        console.log("Updated Agreements after checkbox change:", updatedAgreements);
        setAgreements(updatedAgreements);
    };

    const validateAgreements = () => {
        let isValid = true;
        let findAgreement = agreements.find(a =>
            !a.ESigned ||
            (a.OnboardingDocumentId === 0 && !a.signUploaded)
        );

        if (findAgreement) {
            if (!findAgreement.ESigned) {
                toast.error(`Please agree to the "${findAgreement.DocumentName}" to proceed.`);
            } else if (!findAgreement.signUploaded) {
                toast.error(`Please upload a signature for the "${findAgreement.DocumentName}" agreement to proceed.`);
            }
            isValid = false;
        }
        return isValid;
    }

    const handleSave = async () => {
        if (!validateAgreements() || agreements.length === 0) {
            return
        }
        let data = {
            attachmentsList: agreements.map(item => ({
                ...item,
                uploadedFile: item?.uploadedFile?.file,
                DocumentFileName: item?.uploadedFile?.name,
                onboardingId: props.data?.Id || 0,
                eSigned: item.ESigned,
                documentMasterId: item.DocumentMasterId
            }))
        }
        setShowLoader(true);
        let response = await uploadAgreementDocuments(data, isApprovedSeller);
        if (response?.data?.Status === 200) {
            let toastMsg = 'Agreements updated successfully.';
            if (!isViewUserRequest && hasOnboardingData) {
                toastMsg = 'Agreements updated successfully. Our support team will review them shortly and get back to you.';
            }
            toast.success(toastMsg, { autoClose: 5000 });
            fetchAgreements();
            if (!isViewUserRequest) {
                setShowDialog(true);
            }
        } else {
            setShowDialog(false);
            setShowLoader(false);
            toast.error("Failed to update agreements.");
        }
        setShowLoader(false);
    };

    const handleFileChange = (event, agreement) => {
        const file = event.target.files[0];
        if (file) {
            const extension = file.name.split(".").slice(-1).pop();
            const fileSize = Math.round(file.size / 1024);
            if (!allowedExtensions.includes(extension ? extension.toLowerCase() : "")) {
                toast.error("Unsupported File format", 1000);
                return;
            } else if (fileSize > 1024) {
                toast.info("Size should be less than 1MB", 1000);
                return;
            }
            convertFilesToBase64([file]).then((base64Array) => {
                let base64File = base64Array[0];
                let updatedAgreements = agreements.map(a => a.DocumentMasterId === agreement.DocumentMasterId ? { ...a, signUploaded: true, DocumentFileName: file.name, uploadedFile: {file: base64File.file, name: file.name} } : a);
                setAgreements(updatedAgreements);
            }).catch((error) => {
                console.error(error);
            });
        }
    };

    const getSignName = (agreement) => {
        if (agreement.DocumentDescription === "Service Agreement") {
            return 'ServiceAgreementSignature.png';
        } else if (agreement.DocumentDescription === "Wallet Usage Agreement") {
            return 'WalletUsageAgreementSignature.png';
        } else if (agreement.DocumentDescription === "Credit Card Agreement") {
            return 'CreditCardAgreementSignature.png';
        }
        return 'Signature.png';
    }

    const handleEsignSave = (dataURL, agreement) => {
        let base64 = dataURL.replace("data:image/png;base64,", ""); // Remove the "data:image/png;base64," prefix
        let signName = getSignName(agreement);
        let updatedAgreements = agreements.map(a => a.DocumentMasterId === agreement.DocumentMasterId ? { ...a, signUploaded: true, DocumentFileName: signName, uploadedFile: {file: base64, name: signName} } : a);
        setAgreements(updatedAgreements);
        setModalOpen({ open: false, agreement: null });
    };

    const handleDelete = async (agreement) => {
        if (isApprovedSeller && agreement.OnboardingDocumentId > 0) {
            return;
        }
        if (agreement.OnboardingDocumentId > 0) {
            let data = {
                onboardingDocumentId: agreement.OnboardingDocumentId,
            }
            setShowLoader(true);
            let res = await deleteOnboardingDocument(data, isApprovedSeller);
            setShowLoader(false);
            if (res?.data?.Status === 200) {
                toast.success("Signature removed successfully.");
                fetchAgreements();
            }
        } else {
            let agreementsList = agreements.map(a => a.DocumentMasterId === agreement.DocumentMasterId ? { ...a, signUploaded: false, uploadedFile: null } : a);
            setAgreements(agreementsList);
        }
    };

    const getDocumentStatusList = async () => {
        setShowLoader(true);
        let response = await getDocumentStatusOptions(isApprovedSeller);
        if (response?.data?.Status === 200) {
            const list = response.data.EntityList || [];
            setDocumentStatusList(list);
        }
        setShowLoader(false);
    };

    const handleStatusChange = async (event, agreement) => {
        let value = event.target.value;
        let updatedAgreements = agreements.map(a => a.DocumentMasterId === agreement.DocumentMasterId ? { ...a, StatusId: value } : a);
        setAgreements(updatedAgreements);
        let data = {
            "onboardingDocumentId": agreement.OnboardingDocumentId,
            "documentStatusId": value,
            "modifiedBy": 0
        }
        setShowLoader(true);
        let response = await updateAgreementStatus(data, isApprovedSeller);
        setShowLoader(false);
        if (response?.data?.Status === 200) {
            fetchAgreements();
            toast.success("Document status updated successfully.");
        }
    };

    const handleOpen = (imageUrl) => {
        const newWindow = window.open();
        newWindow.document.write(`<img src="${imageUrl}" alt="Signature" />`);
    }

    const linkifyFileName = (agreement) => {
        if (agreement.DocumentFileUrl) {
            return <a href={agreement.DocumentFileUrl} target="_blank" rel="noopener noreferrer">{agreement.DocumentFileName}</a>;
        } else if (agreement?.signUploaded) {
            const imageUrl = `data:image/png;base64,${agreement?.uploadedFile?.file}`;
            return <button className="sortBtn link" onClick={() => handleOpen(imageUrl)}>{agreement.DocumentFileName}</button>;
        }
    };

    const handleOpenAgreement = async (agreement) => {
        let payloadData = {
            documentMasterId: agreement.DocumentMasterId,
            documentTypeId: 1,
            onboardingId: props.data?.Id || 0,
            onboardingDocumentId: agreement.OnboardingDocumentId
        }
        setShowLoader(true);
        let response = await downloadAgreement(payloadData, isApprovedSeller);
        setShowLoader(false);
        if (response?.data?.Status === 200 && response.data.Entity) {
            downloadPDF(response.data.Entity.pdfBase64, response?.data?.Entity?.fileName);
            toast.success('Agreement downloaded successfully.');
        } else {
            toast.error('Failed to download agreement.');
        }
    };

    const renderHtmlContent = () => {
        return (
            <>
                <p className="mt-2">Your onboarding details and documents have been submitted.{hasOnboardingData ? ' Your account will be approved shortly.' : ''}</p>
            </>
        )
    }

    return (
        <Container fluid>
            <Paper className='onboardingFormWrapping fw-3' elevation={2} square>
                <Typography variant="h6" gutterBottom>
                    Agreements &amp; E-Signatures
                </Typography>
                {showLoader && (
                    <div className="full-screen-spinner-overlay">
                        <CircularProgress disableShrink />
                    </div>
                )}
                <Modal
                    open={modalOpen.open}
                    aria-labelledby="simple-modal-title"
                    aria-describedby="simple-modal-description"
                    closeAfterTransition
                    onClose={() => setModalOpen({ open: false, agreement: null })}
                >
                    <div className="modalWrapper">
                        <SignaturePad 
                            title="3PL Service Agreement"
                            onSave={(dataURL) => {
                                handleEsignSave(dataURL, modalOpen.agreement);
                            }}
                            onClose={() => setModalOpen({ open: false, agreement: null })}
                        />
                    </div>
                </Modal>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>View PDF</TableCell>
                                <TableCell>E-sign / “I Agree”</TableCell>
                                <TableCell>Upload / Signature Preview</TableCell>
                                <TableCell>Signed Date</TableCell>
                                <TableCell>Status</TableCell>
                                
                                {isViewUserRequest && <TableCell>Approval Status</TableCell>}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {agreements.map((agreement, index) => (
                                <TableRow key={agreement.DocumentMasterId}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{agreement.DocumentName}</TableCell>

                                    <TableCell>
                                        <button className="sortBtn tableLink link ml-0" onClick={() => handleOpenAgreement(agreement)}>View Agreement</button>
                                    </TableCell>

                                    <TableCell>
                                        <Checkbox
                                            checked={!!agreement.ESigned}
                                            onChange={(e) => handleCheckbox(e, agreement)}
                                            disabled={isApprovedSeller && !!agreement.ESigned}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {agreement?.DocumentFileUrl || agreement?.signUploaded ? (
                                            <>
                                                <Chip
                                                    data-testid={`uploaded-chip-${agreement.DocumentMasterId}`}
                                                    deleteIcon={
                                                        <Tooltip title="Delete Signature" arrow>
                                                            <CancelIcon style={{ color: "red" }} data-testid={`uploaded-chip-delete-${agreement.DocumentMasterId}`} />
                                                        </Tooltip>
                                                    }
                                                    variant="outlined"
                                                    size="small"
                                                    onDelete={isApprovedSeller && agreement.OnboardingDocumentId > 0 ? undefined : () => handleDelete(agreement)}
                                                    icon={<AttachFileIcon />}
                                                    label={<span className="onboarding-ellipsis" title={agreement.DocumentFileName}>
                                                        {linkifyFileName(agreement)}
                                                    </span>}
                                                    className="onboarding-chip"
                                                />
                                            </>
                                        ) : (
                                            <>
                                                    <IconButton component="label">
                                                        <input
                                                                data-testid={`file-input-${agreement.DocumentMasterId}`}
                                                                type="file"
                                                                hidden
                                                                onChange={(e) => handleFileChange(e, agreement)}
                                                            />
                                                        <Tooltip title="Upload Signature" arrow>
                                                            <CloudUploadIcon />
                                                        </Tooltip>
                                                    </IconButton>
                                                <Tooltip title="Use E-Signature" arrow>
                                                    <IconButton data-testid={`esign-btn-${agreement.DocumentMasterId}`} onClick={() => toggleModal(agreement)}>
                                                        <CreateIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </TableCell>

                                    <TableCell>{agreement.SignedDate ? moment(agreement.SignedDate).format("DD MMM YYYY") : "N/A"}</TableCell>

                                    <TableCell>
                                        {agreement.StatusName === 'Flagged' && <FlagIcon className="mr-1" fontSize="small" style={{ color: "#d32f2f" }} />}
                                        {agreement.StatusName === 'Pending' && <HourglassEmptyIcon className="mr-1" fontSize="small" style={{ color: "#fbc02d" }} />}
                                        {agreement.StatusName === 'Approved' && <CheckCircleIcon className="mr-1" fontSize="small" style={{ color: "#4caf50" }} />}
                                        {agreement.StatusName === 'Rejected' && <CancelIcon className="mr-1" fontSize="small" style={{ color: "#d32f2f" }} />}
                                        {agreement.StatusName === 'Not Uploaded' && <CloudUploadIcon className="mr-1" fontSize="small" style={{ color: "#d32f2f" }} />}
                                        {agreement.StatusName}
                                    </TableCell>

                                    {isViewUserRequest && (
                                        <TableCell>
                                            <Select
                                                data-testid={`status-select-${agreement.DocumentMasterId}`}
                                                onChange={(e) => handleStatusChange(e, agreement)}
                                                value={agreement.StatusId || ''}
                                                labelId="demo-customized-select-label"
                                                id="demo-customized-select"
                                                input={<BootstrapInput />}
                                                disabled={!agreement.OnboardingDocumentId}
                                                style={{ width: "100%" }}
                                            >
                                                {documentStatusList.map(status => (
                                                    <MenuItem  key={status.StatusId} value={status.StatusId}>{status.StatusName}</MenuItem>
                                                ))}
                                            </Select>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        <div className='flexPayment mt-2'>
        {!isViewUserRequest ? <Typography component="em">* Please complete the form to finalize your onboarding process</Typography> : <Typography component="em"></Typography>}
        <div className="mt-3 alignItemsCenter">
            <Button disabled={activeStep === 0} onClick={handleBack} disableElevation>
                Back
            </Button>
            <Button
                disableElevation
                variant="contained"
                color="primary"
                onClick={handleSave}
                className='ml-2 cus-seconday-bg-btn btn'
                disabled={agreements.length === 0}
            >
                Save
            </Button>
            </div>
        </div>

        <AlertDialogModal
            modalShow={showDialog}
            headingText={"Information Saved Successfully"}
            messageText={renderHtmlContent()}
            default={true}
            zIndex={1400}
            handleCancelDeleteAlert={() => {
                setShowDialog(false);
                if (hasOnboardingData) {
                    clearLocalStorage();
                    globalThis.location.reload();
                } else {
                    removeLocalStorageItem('activeStep');
                    navigate('/');
                }
            }}
            isHtml={true}
        />
        </Container>
    );
};

export default AgreementsESignatures;

AgreementsESignatures.propTypes = {
    isViewUserRequest: PropTypes.bool,
    isApprovedSeller: PropTypes.bool,
    activeStep: PropTypes.number,
    handleBack: PropTypes.func,
    data: PropTypes.object
};
